import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { useQueryClient } from '@tanstack/react-query';

const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 1 day
const LS_LAST_ACTIVITY = 'lastActivity';
const ACTIVITY_DEBOUNCE_MS = 300;

/**
 * Events that count as activity.
 *
 * NOTE: the Angular version listened for 'mouseMove', which is not a DOM event
 * name. The real event is 'mousemove', all lowercase, so mouse movement never
 * reset the timer. Fixed here. If you want to preserve the old behaviour
 * exactly, drop 'mousemove' from this list.
 *
 * Split in two because of what happens when one arrives after the session has
 * already lapsed. Clicks and key presses are what trigger actions in the app,
 * so those are stopped before the app sees them (see onActivity). That needs
 * a non-passive listener. The others can't trigger anything on their own, and
 * must stay passive: a non-passive scroll or touchstart listener makes the
 * browser wait for it on every scroll, which is visibly janky on phones.
 */
const ACTION_EVENTS = ['click', 'keypress'] as const;
const PASSIVE_EVENTS = ['mousemove', 'scroll', 'touchstart'] as const;

/** Null when no activity has ever been recorded in this browser. */
export function getLastActivity(): number | null {
  const raw = localStorage.getItem(LS_LAST_ACTIVITY);
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * No recorded activity means this is a new session, not an ancient one.
 *
 * The earlier version defaulted a missing value to 0, which made
 * `Date.now() - 0 >= 24h` true and expired every first-ever sign-in
 * immediately. Angular got away with the same default because AuthService was
 * constructed before login, when signing out was a no-op.
 */
export function isSessionExpired(): boolean {
  const lastActivity = getLastActivity();
  if (lastActivity === null) return false;
  return Date.now() - lastActivity >= SESSION_TIMEOUT_MS;
}

export function updateLastActivity(): void {
  localStorage.setItem(LS_LAST_ACTIVITY, Date.now().toString());
}

export function clearLastActivity(): void {
  localStorage.removeItem(LS_LAST_ACTIVITY);
}

/** Path the user was trying to reach, preserved across a timeout sign-out. */
export function getCurrentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Port of the inactivity handling in AuthService. Mount once, inside the
 * authenticated tree.
 */
export function useSessionTimeout(enabled: boolean): void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let expiring = false;

    /**
     * `announce` mirrors Angular's isInactivityWhileOnSite: only say "your
     * session expired" when the user was here when it happened. Finding a
     * stale timestamp from a previous visit just returns them to the login
     * screen without an accusatory banner.
     */
    const expire = async (announce: boolean) => {
      // Guards against a burst of events each starting their own sign-out.
      if (cancelled || expiring) return;
      expiring = true;
      try {
        await signOut();
      } catch (error) {
        console.error('Error signing out after inactivity:', error);
      }
      clearLastActivity();
      queryClient.clear();
      navigate(announce ? '/home?sessionExpired=true' : '/home', { replace: true });
    };

    const resetTimer = () => {
      updateLastActivity();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (isSessionExpired()) void expire(true);
      }, SESSION_TIMEOUT_MS);
    };

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Check for expiry before recording new activity.
     *
     * The earlier version recorded every activity as "now" without looking at
     * the previous timestamp first. That was fine only as long as the timer
     * fired on schedule, and browser timers don't always: on macOS, Chrome's
     * timers stop counting while the machine sleeps. Close the laptop on
     * Friday, open it Monday, move the mouse, and that movement renewed a
     * session that should have ended days earlier.
     *
     * The check runs on every event, not after the debounce: waiting even
     * 300ms would let the click that woke the page through first.
     */
    const onActivity = (event: Event) => {
      if (expiring || isSessionExpired()) {
        // Swallow the action that arrived on a lapsed session. These listeners
        // run in the capture phase on document, before React sees the event,
        // so stopping it here means the button or link never fires.
        if ((ACTION_EVENTS as readonly string[]).includes(event.type)) {
          event.preventDefault();
          event.stopPropagation();
        }
        void expire(true);
        return;
      }

      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        resetTimer();
      }, ACTIVITY_DEBOUNCE_MS);
    };

    if (isSessionExpired()) {
      // A real timestamp, old enough to have lapsed while they were away.
      void expire(false);
    } else {
      // Either fresh activity or a first sign-in; both start the clock.
      resetTimer();
    }

    // Capture phase, so these run before any handler in the app.
    for (const eventName of ACTION_EVENTS) {
      document.addEventListener(eventName, onActivity, { capture: true });
    }
    for (const eventName of PASSIVE_EVENTS) {
      document.addEventListener(eventName, onActivity, { capture: true, passive: true });
    }

    return () => {
      cancelled = true;
      // removeEventListener only matches if capture matches too.
      for (const eventName of [...ACTION_EVENTS, ...PASSIVE_EVENTS]) {
        document.removeEventListener(eventName, onActivity, { capture: true });
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, navigate, queryClient]);
}
